import { ExclamationCircleOutlined, DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import backBtn from "../../assets/images/back-arrow.svg";
import UserIcon from "../../assets/images/icons/userLogo.svg";
import plusIcon from "../../assets/images/plusIcon.svg";
import sliderPlaceholder from "../../assets/images/slider_placeholder.svg";
import ImageLabel from "../../common/image-label";
import Line from "../../common/line";
import { useConnectsUForm } from "../../custom-hooks/useConnectsUForm";
import { useTab } from "../../custom-hooks/useTab";
import { useStore } from "../../store";
import { setUpdatedValues } from "../../store/actions";
import { getFormFieldValue, rotateArrayByOne } from "../../utils";
import './carousel.scss'
import { formatData } from "../../utils/formatData";
import ImageSlider from "../slider/ImageSlider";
import "./index.scss";
import { inputFieldCharLimit } from "../../config";
import { debounce } from "lodash";

export default function EditVideos() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const whichTab = "corousel";

  const [tabData, setTabData] = useState([{ title: "" }]);
  const [loading, setLoading] = useState(true);
  const [disabledStatus, setDisabledStatus] = useState(true);
  const [titleCount, setTitleCount] = useState(0);

  // new states
  const [hasUnsavedItem, setHasUnsavedItem] = useState(false);
  const [canAddNew, setCanAddNew] = useState(true);

  const { callMicroServices, deleteSingleItem } = useTab();
  const { updateImage } = useConnectsUForm();
  const { updatedValues, dispatch } = useStore();

  sessionStorage.setItem("page", "edit-carousel");

  // ----------------- Utilities -----------------
  const findValue = (i, key) => getFormFieldValue(form, whichTab, i, key);

  const SelectChangeHandler = (e, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], "");
  };

  // ----------------- Data Fetch -----------------
  const getCarouselData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${sessionStorage.getItem("token")}` };
      const response = await getRequest(userServiceApi.getCarousel(), headers);
      if (response) {
        const { data } = response
        if (data?.length) {
          setTabData(rotateArrayByOne(data))
        }
      }
    } catch (error) {
      toast.error(error?.error);
    } finally {
      setLoading(false);
    }
  };

  // ----------------- Debounced Field Change -----------------
  const fieldsChange = debounce((changedFields) => {
    if (changedFields && changedFields.length === 1) {
      const getNames = changedFields[0].name;
      const nameModified = getNames[0] === "corousel" ? "corousel" : getNames[0];
      let prev = { ...updatedValues };

      const prevItems = prev[nameModified];
      if (prevItems) {
        const names = [...prevItems, getNames[1]];
        prev[nameModified] = [...new Set(names)];
      } else {
        prev[nameModified] = [getNames[1]];
      }

      // clear field errors to prevent stale validation issues
      const getAllFieldErrors = form.getFieldsError();
      if (getAllFieldErrors && getAllFieldErrors.length > 0) {
        const updatedFields = getAllFieldErrors.map((field) => ({
          ...field,
          errors: [],
        }));
        form.setFields(updatedFields);
      }
      dispatch(setUpdatedValues(prev));
      refreshUnsavedState(); // added: keep unsaved tracking updated
    }
  }, 300);

  // ----------------- Save Handler -----------------
  const handleUpdateItems = async (data) => {
    if (!data["corousel"]) return;

    let existingData = [],
      newData = [];
    data["corousel"].forEach((item) => {
      item.id ? existingData.push(item) : newData.push(item);
    });

    const formattedData = formatData("corousel", data["corousel"], updatedValues);
    if (Object.keys(formattedData).length > 0) {
      dispatch(setUpdatedValues({}));
      callMicroServices("corousel", formattedData, form);
      setTimeout(() => {
        getCarouselData();
      }, 2000);
    } else {
      toast.error("Nothing to update");
    }
  };

  // ----------------- Enhanced Handlers -----------------
  const refreshUnsavedState = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const unsavedItem = currentList.find(
      (item) =>
        !item?.id &&
        (item?.title?.trim() ||
          item?.mediaLink?.url?.trim() ||
          item?.mediaLink?.mediaType ||
          item?.blob)
    );
    setCanAddNew(!unsavedItem);
    setHasUnsavedItem(!!unsavedItem);
  };

  const handleAddMore = () => {
    if (!canAddNew) {
      message.warning("You must save the current item before you can add a new one.");
      return;
    }

    const currentList = form.getFieldValue(whichTab) || [];
    const unsavedItem = currentList.find(
      (item) =>
        !item?.id &&
        (item?.title?.trim() ||
          item?.mediaLink?.url?.trim() ||
          item?.mediaLink?.mediaType ||
          item?.blob)
    );
    if (unsavedItem) {
      message.warning("You must save the current item before you can add a new one.");
      return;
    }

    let newList = [...currentList]
    newList?.splice(1, 0, { title: "", mediaLink: { mediaType: undefined, url: "" }, media: null })

    form.setFieldsValue({ [whichTab]: newList });
    setCanAddNew(false);
    setHasUnsavedItem(true);
    setDisabledStatus(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = () => {
    form.submit();
    setTimeout(() => {
      setCanAddNew(true);
      setHasUnsavedItem(false);
    }, 500);
  };

  const handleClear = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const updatedList = currentList.map((item) =>
      !item?.id
        ? { title: "", mediaLink: { mediaType: undefined, url: "" }, media: null }
        : item
    );
    form.setFieldsValue({ [whichTab]: updatedList });
    refreshUnsavedState();
  };

  const handleDelete = (i, name, remove) => {
    const item = form.getFieldValue(whichTab)?.[i];
    const id = item?.id;
    if (id) {
      deleteSingleItem(whichTab, id, form);
    } else {
      remove(name);
    }
    setTimeout(() => {
      refreshUnsavedState();
    }, 100);
  };

  // ----------------- Effects -----------------
  useEffect(() => {
    getCarouselData();
  }, []);

  useEffect(() => {
    form.resetFields();
  }, [tabData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tabData?.length === 0) {
        setHasUnsavedItem(true);
      } else {
        setHasUnsavedItem(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [tabData]);

  // ----------------- UI -----------------
  if (loading) {
    return (
      <div className="data-loader">
        <Spin className="loading-svg" tip="Loading" size="large" />
      </div>
    );
  }

  return (
    <div className="tabContainer">
      <div className="main-container" style={{ paddingTop: "5px" }}>
        <div className="header-container">
          <div className="backbtn-container">
            <img src={backBtn} onClick={() => navigate("/partner-org")} alt="" />
            <p>Edit carousel</p>
          </div>
        </div>

        <div className="orgDetails editCarousel">
          <div className="orgDetailsRow">
            <div className="orgDetailsCol">
              <div className="formInfoStyle">
                <div className="formInfoRow">
                  <div className="formInfoCol">
                    <Form
                      name="control-hooks"
                      className="organizationForm"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 18 }}
                      form={form}
                      autoComplete="off"
                      onFinish={handleUpdateItems}
                      onFieldsChange={fieldsChange}
                    >
                      <Form.List
                        name="corousel"
                        initialValue={tabData && tabData.length > 0 ? tabData : [""]}
                      >
                        {(fields, { add, remove }) => (
                          <>
                            <div
                              className="more-btn-container"
                              style={{ marginBottom: "15px" }}
                            >

                              {hasUnsavedItem && (
                                <Button
                                  className="form-clear-btn"
                                  type="button"
                                  onClick={handleClear}
                                >
                                  Clear
                                </Button>
                              )}

                              <Button className="form-add-btn" onClick={handleAddMore}>
                                <img src={plusIcon} alt="" />
                                Add More
                              </Button>
                            </div>

                            <div className="text-center mb-3 fs-6 fw-bold">Main Video/Image</div>

                            {fields.map(({ key, name, ...restField }, i) => (
                              <Space key={key} style={{ display: "block", marginBottom: 0 }}>
                                {/* Title */}
                                <div style={{ position: "relative" }}>
                                  <Form.Item
                                    {...restField}
                                    className={`edit-videos-title showCountStyle ${i === 0 ? 'main-carousel-text' : ''}`}
                                    label="Video/Image Title"
                                    name={[name, "title"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Video/Image Title is required",
                                      },
                                    ]}
                                  >
                                    <Input
                                      placeholder="Video/Image Title"
                                      maxLength={30}
                                      showCount
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    className={`edit-videos-title showCountStyle ${i === 0 ? 'main-carousel-text' : ''}`}
                                    label="Find out more Link"
                                    name={[name, "titleLink"]}
                                    rules={[
                                      {
                                        type: "url",
                                        message: "Please enter a valid URL (https://example.com)",
                                      },
                                    ]}
                                  >
                                    <Input
                                      placeholder="Enter video or image URL"
                                      maxLength={200}
                                    />
                                  </Form.Item>

                                  <Form.Item className={`actionIconStyle ${i === 0 ? 'main-carousel-text' : ''}`}>
                                    {tabData.length > 1 && (
                                      <div className="edit-remove-btn">
                                        <DeleteOutlined
                                          onClick={() => handleDelete(i, name, remove)}
                                        />
                                      </div>
                                    )}
                                  </Form.Item>
                                </div>

                                {/* Media type selector */}
                                <div style={{ position: "relative" }}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "mediaLink", "mediaType"]}
                                    className={`edit-videos-title ${i === 0 ? 'main-carousel-text' : ''}`}
                                    label="Video/Image"
                                    style={{ display: "block" }}
                                    rules={[
                                      { required: true, message: "Select any option" },
                                    ]}
                                  >
                                    <Select
                                      onChange={(e) => SelectChangeHandler(e, i)}
                                      placeholder="Select Option"
                                    >
                                      <Select.Option value="VIMEO_MEDIA">
                                        Add Vimeo Link
                                      </Select.Option>
                                      <Select.Option value="SOCIAL_MEDIA">
                                        Add Youtube Link
                                      </Select.Option>
                                      <Select.Option value="SYNTHESIA_MEDIA">Add Synthesia Link</Select.Option>
                                      <Select.Option value="IMAGE">
                                        Upload Image
                                      </Select.Option>
                                    </Select>
                                  </Form.Item>
                                  <Form.Item className="actionIconStyle">
                                    <div className="edit-remove-btn">
                                      <Tooltip title="Upload Video link or image with size lesser than 2.5MB. For best results, use images and videos with a 16:9 aspect ratio">
                                        <QuestionCircleOutlined />
                                      </Tooltip>
                                    </div>
                                  </Form.Item>
                                </div>

                                {/* URL / Upload */}
                                {findValue(i, "type") === "SOCIAL_MEDIA" ? (
                                  <Form.Item
                                    className={`${i === 0 ? 'main-carousel-text' : ''}`}
                                    label="Youtube Link"
                                    {...restField}
                                    name={[name, "mediaLink", "url"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Youtube link is required",
                                      },
                                    ]}
                                  >
                                    <Input
                                      placeholder="Youtube Link"
                                      maxLength={inputFieldCharLimit}
                                    />
                                  </Form.Item>
                                ) : findValue(i, "type") === "VIMEO_MEDIA" ? (
                                  <Form.Item
                                    className={`${i === 0 ? 'main-carousel-text' : ''}`}
                                    label="Vimeo Link"
                                    {...restField}
                                    name={[name, "mediaLink", "url"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Vimeo link is required",
                                      },
                                    ]}
                                  >
                                    <Input
                                      placeholder="Vimeo Link"
                                      maxLength={inputFieldCharLimit}
                                    />
                                  </Form.Item>
                                ) : findValue(i, "type") === "IMAGE" ? (
                                  <Form.Item
                                    {...restField}
                                    label={<ImageLabel label="Image" />}
                                    colon={false}
                                    name="media"
                                    className={`experienceImg ${i === 0 ? 'main-carousel-text' : ''}`}
                                    rules={[
                                      {
                                        validator: (_) =>
                                          findValue(i, "blob") ||
                                            findValue(i, "url")
                                            ? Promise.resolve()
                                            : Promise.reject(
                                              new Error("Image is required")
                                            ),
                                      },
                                    ]}
                                  >
                                    {findValue(i, "blob") ? (
                                      <img
                                        className="form-image"
                                        src={URL.createObjectURL(findValue(i, "blob"))}
                                        alt=""
                                      />
                                    ) : findValue(i, "url") ? (
                                      <img
                                        className="form-image"
                                        src={findValue(i, "url")}
                                        alt=""
                                      />
                                    ) : (
                                      <img className="form-image" src={UserIcon} alt="" />
                                    )}

                                    <Tooltip title="Upload image with size lesser than 2.5MB">
                                      <label className="fileUploadBtn">
                                        Upload Image
                                        <input
                                          accept="image/png, image/gif, image/jpeg"
                                          type="file"
                                          onChange={(e) =>
                                            updateImage(e, i, form, whichTab)
                                          }
                                        />
                                      </label>
                                    </Tooltip>
                                    <Tooltip title="Use a image with 16:9 aspect ratio for best results">
                                      <ExclamationCircleOutlined
                                        style={{ marginLeft: 8, cursor: "pointer" }}
                                      />
                                    </Tooltip>
                                  </Form.Item>
                                ) : findValue(i, "type") === "SYNTHESIA_MEDIA" ? (
                                  <Form.Item
                                    className={`${i === 0 ? 'main-carousel-text' : ''}`}
                                    label="Synthesia Link"
                                    labelCol={{ span: 8 }}
                                    {...restField}
                                    name={[name, "mediaLink", "url"]}
                                  >
                                    <Input />
                                  </Form.Item>
                                ) : null}

                                <Line />
                              </Space>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </Form>
                  </div>
                </div>
              </div>
            </div>

            <div className="orgDetailsCol">
              <div className="formInfoStyle">
                <div className="formInfoRow">
                  <div className="formInfoCol">
                    {tabData?.length > 0 ? (
                      <ImageSlider data={tabData} />
                    ) : (
                      <div style={{ width: "100%", transform: "scale(0.8)" }}>
                        <img src={sliderPlaceholder} alt="placeholder" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save / Cancel */}
        <div
          style={{ position: "fixed", bottom: "10px", right: "10px" }}
          className="form-btn-container"
        >
          <Button className="form-cancel-btn" onClick={() => navigate("/partner-org")}>
            Cancel
          </Button>
          <Button className="form-save-btn" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
